import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Download, 
  Users, 
  ClipboardList, 
  GitBranch,
  FileSpreadsheet,
  Loader2,
  CheckCircle,
  AlertCircle,
  Filter,
  X
} from "lucide-react";
import { API } from "@/config/api";
import { AREAS, REP_DIRECTORY, MEDICAL_AID_OPTIONS } from "@/config/constants";
import LocationSelector from "@/components/LocationSelector";

const ExportPage = () => {
  const [downloading, setDownloading] = useState({
    doctors: false,
    visits: false,
    referrals: false
  });
  
  const [lastExport, setLastExport] = useState({
    doctors: null,
    visits: null,
    referrals: null
  });

  // Filter states - use "ALL" instead of "" to avoid Radix Select.Item error
  const [doctorFilters, setDoctorFilters] = useState({
    city: "",
    area: "",
    suburb: "",
    status: "ALL"
  });

  const [visitFilters, setVisitFilters] = useState({
    rep: "ALL",
    city: "",
    area: "",
    suburb: "",
    start_date: "",
    end_date: ""
  });

  const [referralFilters, setReferralFilters] = useState({
    rep: "ALL",
    city: "",
    area: "",
    suburb: "",
    medical_aid: "ALL",
    start_date: "",
    end_date: ""
  });

  // Clean filters - convert "ALL" to empty string for API
  const cleanFilters = (filters) => {
    const cleaned = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "ALL" && value.trim()) {
        cleaned[key] = value;
      }
    });
    return cleaned;
  };

  // Fetch-based download that sends cookies for authentication
  const downloadFile = async (endpoint, params, type) => {
    setDownloading(prev => ({ ...prev, [type]: true }));
    
    try {
      const cleanedParams = cleanFilters(params);
      const queryParams = new URLSearchParams();
      Object.entries(cleanedParams).forEach(([key, value]) => {
        queryParams.append(key, value);
      });
      
      const url = `${API}/exports/${endpoint}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',  // THIS sends cookies with the request
      });
      
      if (!response.ok) {
        throw new Error(`Export failed: ${response.status}`);
      }
      
      // Get filename from Content-Disposition header
      const disposition = response.headers.get('Content-Disposition') || '';
      const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      const filename = filenameMatch ? filenameMatch[1].replace(/['"]/g, '') : `${endpoint}_export.xlsx`;
      
      // Convert response to blob and trigger download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      setLastExport(prev => ({ ...prev, [type]: new Date() }));
    } catch (error) {
      console.error('Export error:', error);
      alert(`Export failed: ${error.message}. Please try again.`);
    } finally {
      setDownloading(prev => ({ ...prev, [type]: false }));
    }
  };

  const formatExportTime = (date) => {
    if (!date) return null;
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const hasFilters = (filters) => {
    return Object.entries(filters).some(([key, v]) => {
      // Date fields use empty string, select fields use "ALL" for no filter
      if (key === 'start_date' || key === 'end_date') {
        return v && v.trim();
      }
      return v && v !== "ALL" && v.trim();
    });
  };

  const clearFilters = (setFilters, defaultFilters) => {
    setFilters(defaultFilters);
  };

  // Status options for doctors
  const STATUS_OPTIONS = ["Active", "Inactive", "Retired", "Moved", "Deceased"];

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <FileSpreadsheet className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Data Exports</h1>
          <p className="text-muted-foreground text-sm">
            Download filtered Excel files for analysis or re-import
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Smart Filtered Exports</p>
          <p className="mt-1 text-blue-700">
            Apply filters to export only the data you need. Leave filters empty to export all records.
            Files are in .xlsx format with clean headers matching the import structure.
          </p>
        </div>
      </div>

      {/* Export Cards */}
      <div className="grid gap-6">
        
        {/* Doctors Export */}
        <Card data-testid="export-card-doctors">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Doctors Export</CardTitle>
                  <CardDescription>Export active doctors from the Rolodex</CardDescription>
                </div>
              </div>
              {lastExport.doctors && (
                <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200 bg-emerald-50">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {formatExportTime(lastExport.doctors)}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Filters */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Filter className="w-4 h-4" />
                  Filters
                </div>
                {hasFilters(doctorFilters) && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => clearFilters(setDoctorFilters, { city: "", area: "", suburb: "", status: "ALL" })}
                    className="h-7 text-xs"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                <LocationSelector
                  city={doctorFilters.city}
                  area={doctorFilters.area}
                  suburb={doctorFilters.suburb}
                  onLocationChange={(loc) => setDoctorFilters(p => ({ ...p, city: loc.city, area: loc.area, suburb: loc.suburb }))}
                  required={false}
                  data-testid="export-doctors-location-filter"
                />
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={doctorFilters.status} onValueChange={(v) => setDoctorFilters(p => ({ ...p, status: v }))}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All statuses</SelectItem>
                      {STATUS_OPTIONS.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">Columns:</span> AREA, ID Goodx, Status, medibase, title, Initials, Surname, practice #, MP Number, Tel #, Sel #, Fax #, Email, Address, Postal Address, Qualification
            </div>
            
            <Button 
              onClick={() => downloadFile('doctors', doctorFilters, 'doctors')}
              disabled={downloading.doctors}
              className="w-full"
              data-testid="export-btn-doctors"
            >
              {downloading.doctors ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Preparing Download...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download Doctors (.xlsx)
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Visits Export */}
        <Card data-testid="export-card-visits">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <ClipboardList className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Visits Export</CardTitle>
                  <CardDescription>Export logged rep visits with doctor details</CardDescription>
                </div>
              </div>
              {lastExport.visits && (
                <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200 bg-emerald-50">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {formatExportTime(lastExport.visits)}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Filters */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Filter className="w-4 h-4" />
                  Filters
                </div>
                {hasFilters(visitFilters) && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => clearFilters(setVisitFilters, { rep: "ALL", city: "", area: "", suburb: "", start_date: "", end_date: "" })}
                    className="h-7 text-xs"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Rep</Label>
                    <Select value={visitFilters.rep} onValueChange={(v) => setVisitFilters(p => ({ ...p, rep: v }))}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All reps" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All reps</SelectItem>
                        {REP_DIRECTORY.map(rep => (
                          <SelectItem key={rep} value={rep}>{rep}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <LocationSelector
                  city={visitFilters.city}
                  area={visitFilters.area}
                  suburb={visitFilters.suburb}
                  onLocationChange={(loc) => setVisitFilters(p => ({ ...p, city: loc.city, area: loc.area, suburb: loc.suburb }))}
                  required={false}
                  data-testid="export-visits-location-filter"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">From Date</Label>
                    <Input
                      type="date"
                      value={visitFilters.start_date}
                      onChange={(e) => setVisitFilters(p => ({ ...p, start_date: e.target.value }))}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">To Date</Label>
                    <Input
                      type="date"
                      value={visitFilters.end_date}
                      onChange={(e) => setVisitFilters(p => ({ ...p, end_date: e.target.value }))}
                      className="h-9"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">Columns:</span> Rep Name, Doctor Name, Practice Name, Area, Visit Date, Visit Type, Materials Used, Saw Doctor, Gatekeeper Seen, Next Appointment Date, Notes
            </div>
            
            <Button 
              onClick={() => downloadFile('visits', visitFilters, 'visits')}
              disabled={downloading.visits}
              className="w-full"
              data-testid="export-btn-visits"
            >
              {downloading.visits ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Preparing Download...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download Visits (.xlsx)
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Referrals Export */}
        <Card data-testid="export-card-referrals">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <GitBranch className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Referrals Export</CardTitle>
                  <CardDescription>Export ALL referrals including those not yet commissioned</CardDescription>
                </div>
              </div>
              {lastExport.referrals && (
                <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200 bg-emerald-50">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {formatExportTime(lastExport.referrals)}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Filters */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Filter className="w-4 h-4" />
                  Filters
                </div>
                {hasFilters(referralFilters) && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => clearFilters(setReferralFilters, { rep: "ALL", city: "", area: "", suburb: "", medical_aid: "ALL", start_date: "", end_date: "" })}
                    className="h-7 text-xs"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Assigned Rep</Label>
                    <Select value={referralFilters.rep} onValueChange={(v) => setReferralFilters(p => ({ ...p, rep: v }))}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All reps" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All reps</SelectItem>
                        {REP_DIRECTORY.map(rep => (
                          <SelectItem key={rep} value={rep}>{rep}</SelectItem>
                        ))}
                        <SelectItem value="Unassigned">Unassigned</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <LocationSelector
                  city={referralFilters.city}
                  area={referralFilters.area}
                  suburb={referralFilters.suburb}
                  onLocationChange={(loc) => setReferralFilters(p => ({ ...p, city: loc.city, area: loc.area, suburb: loc.suburb }))}
                  required={false}
                  data-testid="export-referrals-location-filter"
                />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Medical Aid</Label>
                    <Select value={referralFilters.medical_aid} onValueChange={(v) => setReferralFilters(p => ({ ...p, medical_aid: v }))}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All medical aids" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        <SelectItem value="ALL">All medical aids</SelectItem>
                        {MEDICAL_AID_OPTIONS.map(aid => (
                          <SelectItem key={aid} value={aid}>{aid}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">From Date</Label>
                    <Input
                      type="date"
                      value={referralFilters.start_date}
                      onChange={(e) => setReferralFilters(p => ({ ...p, start_date: e.target.value }))}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">To Date</Label>
                    <Input
                      type="date"
                      value={referralFilters.end_date}
                      onChange={(e) => setReferralFilters(p => ({ ...p, end_date: e.target.value }))}
                      className="h-9"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">Columns:</span> Referral Date, Name, Surname, TIN Number, Doctor Name, Medical Aid, Area, Assigned Rep, Out of Hospital, Created At, Current Stage, Commission Eligible
            </div>
            
            <Button 
              onClick={() => downloadFile('referrals', referralFilters, 'referrals')}
              disabled={downloading.referrals}
              className="w-full"
              data-testid="export-btn-referrals"
            >
              {downloading.referrals ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Preparing Download...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download Referrals (.xlsx)
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Footer Note */}
      <div className="text-center text-xs text-muted-foreground pt-4">
        Exports are generated in real-time with filtered data. 
        File names include today's date and filter info for easy tracking.
      </div>
    </div>
  );
};

export default ExportPage;
